import { createApi } from "@reduxjs/toolkit/query/react";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { serializeError } from "serialize-error";
import { supabase, supabaseBaseQuery } from "../../../supabase";
import type {
  InitiatePromptRunResponse,
  InitiatePromptRunPayload,
  GetCurrentPromptRunPayload,
  GetCurrentPromptRunResponse,
  GetPromptRunsPayload,
  GetPromptRunsResponse,
} from "../types/promptRunTypes";
import type {
  ExecutionJobs,
  GetExecutionJobsPayload,
  CancelExecutionJobPayload,
  InitiateExecutionJobPayload,
} from "../types/executionJobTypes";
import { promptRunner } from "../classes/promptRunnerInstance";
import { promptApi } from "./promptApi";
import { ExecutionUIStreamReducer } from "../helpers/executionUIStreamReducer";

const promptRunnerApi = createApi({
  reducerPath: "api.PromptRunnerApi",
  tagTypes: ["CurrentRun", "PromptRuns", "ExecutionJobs"],
  baseQuery: supabaseBaseQuery,
  endpoints: (builder) => ({
    getCurrentPromptRun: builder.query<GetCurrentPromptRunResponse, GetCurrentPromptRunPayload>({
      queryFn: async ({ prompt_id, prompt_version_id, prompt_run_id }) => {
        if (prompt_id === "NEW") return { data: null };

        const { data, error } = await supabase
          .rpc("get_latest_prompt_run", {
            p_prompt_id: prompt_id,
            p_prompt_version_id: prompt_version_id,
            p_run_id: prompt_run_id,
          })
          .maybeSingle();

        if (error) return { error: serializeError(error) };

        return { data: data as GetCurrentPromptRunResponse };
      },

      providesTags: (_, __, { prompt_id }) => [{ type: "CurrentRun", id: prompt_id }],
    }),

    getPromptRuns: builder.query<GetPromptRunsResponse, GetPromptRunsPayload>({
      queryFn: async ({ prompt_id }) => {
        const { data, error } = await supabase.rpc("get_prompt_runs", {
          p_prompt_id: prompt_id,
        });

        if (error) return { error: serializeError(error) };

        return { data: data as GetPromptRunsResponse };
      },
      providesTags: (_, __, { prompt_id }) => [{ type: "PromptRuns", id: prompt_id }],
    }),

    initiatePromptRun: builder.mutation<InitiatePromptRunResponse, InitiatePromptRunPayload>({
      queryFn: async (payload) => {
        const { data, error } = await supabase.functions.invoke("initiate-prompt-run", {
          method: "POST",
          body: payload,
        });

        if (error) {
          // https://supabase.com/docs/guides/functions/error-handling#client-side-error-handling
          if (error instanceof FunctionsHttpError) {
            const errorData = await error.context.json();
            return { error: serializeError(errorData) };
          } else {
            return { error: serializeError(error) };
          }
        }

        return { data };
      },

      onQueryStarted: async (payload, { dispatch, queryFulfilled }) => {
        // We need to create an empty list of jobs immediately to prevent
        // the UI from showing execution jobs from previous runs
        await dispatch(
          promptRunnerApi.util.upsertQueryData("getExecutionJobs", { run_id: payload.run_id }, []),
        );

        // Optimistically update the current run to display processing
        // indicator in UI immediately
        const runPatch = dispatch(
          // @ts-ignore - sometimes triggers TS2589 error: Type instantiation is excessively deep and possibly infinite.
          promptRunnerApi.util.updateQueryData(
            "getCurrentPromptRun",
            { prompt_id: payload.prompt_id },
            () => {
              return {
                id: payload.run_id!,
                prompt_id: payload.prompt_id,
                prompt_version_id: undefined,
                prompt_meta: payload.prompt_meta,
                test_cases: payload.test_cases,
                created_by: payload.created_by,
                created_at: new Date().toISOString(),
              };
            },
          ),
        );

        try {
          await queryFulfilled;

          // Invalidate prompt versions cache
          dispatch(
            promptApi.util.invalidateTags([
              {
                type: "PromptVersions",
                id: payload.prompt_id,
              },
            ]),
          );
        } catch {
          runPatch.undo();
        }
      },

      invalidatesTags: (response, __, { prompt_id }) => [
        { type: "ExecutionJobs", id: response?.run_id },
        { type: "CurrentRun", id: prompt_id },
        { type: "PromptRuns", id: prompt_id },
      ],
    }),

    getExecutionJobs: builder.query<ExecutionJobs, GetExecutionJobsPayload>({
      queryFn: async ({ run_id }) => {
        if (!run_id) return { data: [] };

        const { data, error } = await supabase
          .from("llm_execution_jobs")
          .select("*")
          .filter("prompt_run_id", "eq", run_id)
          .order("created_at", { ascending: false });

        if (error) return { error: serializeError(error) };

        return { data: data as unknown as ExecutionJobs };
      },

      async onCacheEntryAdded(_, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        const uiStreamReducer = new ExecutionUIStreamReducer();

        try {
          await cacheDataLoaded;

          const applyUIStreamPatch = (
            executionId: string,
            patch: {
              status?: import("../types/executionJobTypes").ExecutionJob["status"];
              resultPatch: Record<string, unknown>;
            },
          ) => {
            updateCachedData((draft) => {
              // @ts-ignore - sometimes triggers TS2589 error: Type instantiation is excessively deep and possibly infinite.
              const log = draft.find((log) => log.id === executionId);
              if (!log) return;
              if (patch.status) log.status = patch.status;
              log.result = {
                ...log.result,
                ...patch.resultPatch,
              };
            });
          };

          promptRunner.onUIChunk((envelope) => {
            uiStreamReducer.handleChunk(envelope.executionId, envelope.chunk, (patch) => {
              applyUIStreamPatch(envelope.executionId, patch);
            });
          });

          promptRunner.onError((message) => {
            updateCachedData((draft) => {
              // @ts-ignore - sometimes triggers TS2589 error: Type instantiation is excessively deep and possibly infinite.
              const log = draft.find((log) => log.id === message.executionId);

              if (!log) return;

              log.status = message.status;
              log.result = {
                ...log.result,
                error: {
                  ...log.result?.error,
                  code: message.error.code,
                  message: message.error.message,
                },
              };
            });
          });

          promptRunner.onCancel((message) => {
            uiStreamReducer.close(message.executionId, "canceled");
            updateCachedData((draft) => {
              // @ts-ignore - sometimes triggers TS2589 error: Type instantiation is excessively deep and possibly infinite.
              const log = draft.find((log) => log.id === message.executionId);

              if (!log) return;

              log.status = message.status;
            });
          });
        } catch (e) {
          console.error(e);
        }

        await cacheEntryRemoved;
      },

      providesTags: (_, __, { run_id }) => [{ type: "ExecutionJobs", id: run_id }],
    }),

    cancelExecutionJob: builder.mutation<void, CancelExecutionJobPayload>({
      queryFn: async ({ job }) => {
        try {
          promptRunner.cancelExecutionJob(job.id);
          return { data: undefined };
        } catch (error) {
          return { error: serializeError(error as Error) };
        }
      },
    }),

    scheduleExecutionJob: builder.mutation<void, InitiateExecutionJobPayload>({
      queryFn: async (payload) => {
        const { data, error } = await supabase.functions.invoke("schedule-execution-job", {
          method: "POST",
          body: payload,
        });

        if (error) {
          // https://supabase.com/docs/guides/functions/error-handling#client-side-error-handling
          if (error instanceof FunctionsHttpError) {
            const errorData = await error.context.json();
            return { error: serializeError(errorData) };
          } else {
            return { error: serializeError(error) };
          }
        }

        return { data };
      },

      onQueryStarted: async (payload, { dispatch, queryFulfilled }) => {
        // We need to create an empty list of jobs immediately to prevent
        // the UI from showing execution jobs from previous runs
        const jobPatch = dispatch(
          promptRunnerApi.util.updateQueryData(
            "getExecutionJobs",
            { run_id: payload.run_id! },
            (draft) => {
              // @ts-ignore - sometimes triggers TS2589 error: Type instantiation is excessively deep and possibly infinite.
              const job = draft.find((job) => job.id === payload.job_id);

              if (!job) return;

              job.status = "pending";
              job.created_at = new Date().toISOString();
              job.result = null;
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          jobPatch.undo();
        }
      },
    }),
  }),
});

const {
  useGetCurrentPromptRunQuery,
  useGetPromptRunsQuery,
  useInitiatePromptRunMutation,
  useCancelExecutionJobMutation,
  useGetExecutionJobsQuery,
  useScheduleExecutionJobMutation,
} = promptRunnerApi;

export {
  useGetCurrentPromptRunQuery,
  useGetPromptRunsQuery,
  useInitiatePromptRunMutation,
  useCancelExecutionJobMutation,
  useGetExecutionJobsQuery,
  useScheduleExecutionJobMutation,
  promptRunnerApi,
};
